import { Component, Inject, OnDestroy, OnInit, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as Waves from 'node-waves';

import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreLoadingScreenService } from '@core/services/loading-screen.service';
import { CoreTranslationService } from '@core/services/translation.service';

import { menu } from 'app/menu/menu';
import { locale as menuEnglish } from 'app/menu/i18n/en';
import { locale as menuFrench } from 'app/menu/i18n/fr';
import { locale as menuGerman } from 'app/menu/i18n/de';
import { locale as menuPortuguese } from 'app/menu/i18n/pt';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { AuthenticationService } from './auth/service';
import { UserIdleService } from 'angular-user-idle';
import Swal from 'sweetalert2';
import { ConfigConstants } from './main/apps/constants/config-constants';
import { CalendarService } from './main/apps/audit-management/calendar.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('calModal') calModal: any;

  coreConfig: any;
  menu: any;
  defaultLanguage: 'en'; // This language will be used as a fallback when a translation isn't found in the current language
  appLanguage: 'en'; // Set application default language i.e fr

  // Private
  private _unsubscribeAll: Subject<any>;
  private isUserIdle = false;
  private idleActivityListener: () => void;
  private idleActivityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'keyup', 'click', 'visibilitychange'];
  private tokenExpiryInterval: any;
  private calendarReminderInterval: any;
  private wakeRefreshListener: () => void;
  private storageActivityListener: (e: StorageEvent) => void;
  private lastActivityStamp = 0;
  // Must match UserIdleModule.forRoot({ idle }) in app.module.ts.
  private readonly IDLE_SECONDS = 2700;
  private readonly LAST_ACTIVITY_KEY = 'lawtrax_last_activity';

  public events = [];
  public reminder: Number = 5;
  public options = [
    { label: '5 Mins', value: '5' },
    { label: '10 Mins', value: '10' },
    { label: '15 Mins', value: '15' },
    { label: '30 Mins', value: '30' },
    { label: '1 Hour', value: '60' },
    { label: '1 day', value: '1440' },
  ]
  
  modalReference: NgbModalRef;

  /**
   * Constructor
   *
   * @param {DOCUMENT} document
   * @param {Title} _title
   * @param {Renderer2} _renderer
   * @param {ElementRef} _elementRef
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreLoadingScreenService} _coreLoadingScreenService
   * @param {CoreMenuService} _coreMenuService
   * @param {CoreTranslationService} _coreTranslationService
   * @param {TranslateService} _translateService
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    public _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreLoadingScreenService: CoreLoadingScreenService,
    private _coreMenuService: CoreMenuService,
    private _coreTranslationService: CoreTranslationService,
    private _translateService: TranslateService,
    private _afAuth: AngularFireAuth,
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private userIdle: UserIdleService,
    public _calendarService: CalendarService,
    private modelService: NgbModal
  ) {
    // Get the application main menu
    this.menu = menu;

    // Register the menu to the menu service
    this._coreMenuService.register('main', this.menu);

    // Set the main menu as our current menu
    this._coreMenuService.setCurrentMenu('main');

    // Add languages to the translation service
    this._translateService.addLangs(['en', 'fr', 'de', 'pt']);

    // This language will be used as a fallback when a translation isn't found in the current language
    this._translateService.setDefaultLang('en');

    // Set the translations for the menu
    this._coreTranslationService.translate(menuEnglish, menuFrench, menuGerman, menuPortuguese);

    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this._afAuth.onAuthStateChanged((user) => {
      if (!user && !window.location.href.includes('apps/lead-management/lead-intake-form') && !window.location.href.includes('authentication/anonymous-login')) {
        this._authenticationService.logout();
        if (location.href.indexOf('apps') > -1) {
          this._router.navigate([`/pages/authentication/login-v2`], { queryParams: { returnUrl: location.href } });
        }
        else {
          this._router.navigate([`/pages/authentication/login-v2`]);
        }
      }
    });
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Set up listeners for tab/browser close events
    //this.setupSessionCloseHandlers();
    // Init wave effect (Ripple effect)
    Waves.init();

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;

      // Set application default language. 

      // Change application language? Read the ngxTranslate Fix

      // ? Use app-config.ts file to set default language
      const appLanguage = this.coreConfig.app.appLanguage || 'en';
      this._translateService.use(appLanguage);

      // ? OR
      // ? User the current browser lang if available, if undefined use 'en'
      // const browserLang = this._translateService.getBrowserLang();
      // this._translateService.use(browserLang.match(/en|fr|de|pt/) ? browserLang : 'en');

      /**
       * ! Fix : ngxTranslate
       * ----------------------------------------------------------------------------------------------------
       */

      /**
       *
       * Using different language than the default ('en') one i.e French?
       * In this case, you may find the issue where application is not properly translated when your app is initialized.
       *
       * It's due to ngxTranslate module and below is a fix for that.
       * Eventually we will move to the multi language implementation over to the Angular's core language service.
       *
       **/

      // Set the default language to 'en' and then back to 'fr'.

      setTimeout(() => {
        this._translateService.setDefaultLang('en');
        this._translateService.setDefaultLang(appLanguage);
      });

      /**
       * !Fix: ngxTranslate
       * ----------------------------------------------------------------------------------------------------
       */

      // Layout
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove(
        'vertical-layout',
        'vertical-menu-modern',
        'horizontal-layout',
        'horizontal-menu'
      );
      // Add class based on config options
      if (this.coreConfig.layout.type === 'vertical') {
        this._elementRef.nativeElement.classList.add('vertical-layout', 'vertical-menu-modern');
      } else if (this.coreConfig.layout.type === 'horizontal') {
        this._elementRef.nativeElement.classList.add('horizontal-layout', 'horizontal-menu');
      }

      // Navbar
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove(
        'navbar-floating',
        'navbar-static',
        'navbar-sticky',
        'navbar-hidden'
      );

      // Add class based on config options
      if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
        this._elementRef.nativeElement.classList.add('navbar-static');
      } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
        this._elementRef.nativeElement.classList.add('navbar-sticky');
      } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
        this._elementRef.nativeElement.classList.add('navbar-floating');
      } else {
        this._elementRef.nativeElement.classList.add('navbar-hidden');
      }

      // Footer
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

      // Add class based on config options
      if (this.coreConfig.layout.footer.type === 'footer-sticky') {
        this._elementRef.nativeElement.classList.add('footer-fixed');
      } else if (this.coreConfig.layout.footer.type === 'footer-static') {
        this._elementRef.nativeElement.classList.add('footer-static');
      } else {
        this._elementRef.nativeElement.classList.add('footer-hidden');
      }

      // Blank layout
      if (
        this.coreConfig.layout.menu.hidden &&
        this.coreConfig.layout.navbar.hidden &&
        this.coreConfig.layout.footer.hidden
      ) {
        this._elementRef.nativeElement.classList.add('blank-page');
        // ! Fix: Transition issue while coming from blank page
        this._renderer.setAttribute(
          this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
          'style',
          'transition:none'
        );
      } else {
        this._elementRef.nativeElement.classList.remove('blank-page');
        // ! Fix: Transition issue while coming from blank page
        setTimeout(() => {
          this._renderer.setAttribute(
            this._elementRef.nativeElement.getElementsByClassName('app-content')[0],
            'style',
            'transition:300ms ease all'
          );
        }, 0);
        // If navbar hidden
        if (this.coreConfig.layout.navbar.hidden) {
          this._elementRef.nativeElement.classList.add('navbar-hidden');
        }
        // Menu (Vertical menu hidden)
        if (this.coreConfig.layout.menu.hidden) {
          this._renderer.setAttribute(this._elementRef.nativeElement, 'data-col', '1-column');
        } else {
          this._renderer.removeAttribute(this._elementRef.nativeElement, 'data-col');
        }
        // Footer
        if (this.coreConfig.layout.footer.hidden) {
          this._elementRef.nativeElement.classList.add('footer-hidden');
        }
      }

      // Skin Class (Adding to body as it requires highest priority)
      if (this.coreConfig.layout.skin !== '' && this.coreConfig.layout.skin !== undefined) {
        this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
        this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
      }
    });

    // Set the application page title
    this._title.setTitle(this.coreConfig.app.appTitle);

    // Set Length of Badge Title
    if(this.menu[0].id === 'dashboard') {
      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      this.menu[0].badge.title = this.getTitleCount(currentUser);
    }

    this.userIdle.startWatching();

    // Register activity listeners ONCE so resetTimer fires on user activity.
    // Previously these were added inside onTimerStart.subscribe, which leaked
    // hundreds of duplicate listeners over a long session and caused freezes.
    this.idleActivityListener = () => {
      this.userIdle.resetTimer();
      // Broadcast activity to other tabs (throttled to once / 10s). Idle is
      // tracked per-tab, so without this a backgrounded tab would time out and
      // clear the SHARED localStorage session while the user is active elsewhere.
      const now = Date.now();
      if (now - this.lastActivityStamp > 10000) {
        this.lastActivityStamp = now;
        try { localStorage.setItem(this.LAST_ACTIVITY_KEY, String(now)); } catch (e) { /* storage blocked/full */ }
      }
    };
    for (const event of this.idleActivityEvents) {
      document.body.addEventListener(event, this.idleActivityListener);
    }

    // When another tab reports activity, reset THIS tab's idle timer so a
    // backgrounded tab never independently reaches onTimeout while the user is
    // active elsewhere. (storage events fire only in OTHER tabs, not the writer.)
    this.storageActivityListener = (e: StorageEvent) => {
      if (e.key === this.LAST_ACTIVITY_KEY) {
        this.userIdle.resetTimer();
      }
    };
    window.addEventListener('storage', this.storageActivityListener);

    this.userIdle.onTimeout().pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      // Safety net for the per-tab idle problem: before logging out (which
      // clears the SHARED session for ALL tabs), confirm the user is idle
      // GLOBALLY. If any tab saw activity within the idle window, just reset
      // this tab's timer instead of killing everyone's session.
      const last = parseInt(localStorage.getItem(this.LAST_ACTIVITY_KEY) || '0', 10);
      const idleWindowMs = (this.IDLE_SECONDS + ConfigConstants.SESSION_EXPIRE_POPUP_TIME_SECONDS) * 1000;
      if (last && (Date.now() - last) < idleWindowMs) {
        this.userIdle.resetTimer();
        return;
      }
      this.isUserIdle = true;
      this._authenticationService.logout();
      this._router.navigate(['/pages/authentication/login-v2']);
    });

    this.userIdle.onTimerStart().pipe(takeUntil(this._unsubscribeAll)).subscribe((count) => {
      this.isUserIdle = false;
      if (localStorage && localStorage.length > 0
          && !window.location.href.includes('apps/lead-management/lead-intake-form')
          && !window.location.href.includes('authentication/login-v2')) {
        if (count && count < 2) {
          this.refreshTokenMessage();
        }
      }
    });

    this.userIdle.ping$.pipe(takeUntil(this._unsubscribeAll)).subscribe(async () => {
      if (localStorage && localStorage.length > 0
          && !window.location.href.includes('apps/lead-management/lead-intake-form')
          && !window.location.href.includes('authentication/login-v2')) {
        try {
          await this._authenticationService.refreshToken();
        } catch (error) {
          // Only force logout on TERMINAL Firebase auth failures, not transient
          // network/rehydration errors (those recover on the next request/refresh).
          const code = (error && error.code) || '';
          const terminal = [
            'auth/user-disabled',
            'auth/user-token-expired',
            'auth/user-token-revoked',
            'auth/invalid-user-token',
            'auth/user-not-found',
          ];
          if (typeof code === 'string' && terminal.includes(code)) {
            this._authenticationService.logout();
            this._router.navigate(['/pages/authentication/login-v2']);
          }
        }
      }
    });

    this.tokenExpiryInterval = setInterval(async () => {
      if (localStorage.getItem('currentUser')) {
        const tokenExpiryInfo = this._authenticationService.getTokenExpiryInfo(
          this._authenticationService.currentUserValue.token
        );
        if (tokenExpiryInfo && tokenExpiryInfo.timeUntilExpiryMinutes <= 5 && !this.isUserIdle) {
          try {
            await this._authenticationService.refreshToken();
          } catch (error) {
            // swallow — interceptor will handle a real 401 on next request
          }
        }
      }
    }, 30000);

    // Refresh the token when the app regains focus or the network reconnects.
    // setInterval and the SDK's proactive refresh both freeze during sleep/tab
    // suspension, so on wake the cached token can be expired before the next
    // request fires. Refreshing here gets a fresh token (via getIdToken(true) ->
    // onIdTokenChanged -> localStorage mirror) BEFORE the user's first request,
    // avoiding the expired-token 401 entirely. Errors are swallowed — a real 401
    // is still handled by the ErrorInterceptor retry.
    this.wakeRefreshListener = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const cu = this._authenticationService.currentUserValue;
      if (!cu || !cu.token) {
        return;
      }
      // Only refresh when the cached token is close to or past expiry, so we
      // don't fire a forced refresh on every tab switch (avoids rate limiting).
      const info = this._authenticationService.getTokenExpiryInfo(cu.token);
      if (!info || info.timeUntilExpiryMinutes <= 10) {
        this._authenticationService.refreshToken().catch(() => { /* interceptor handles a real 401 */ });
      }
    };
    document.addEventListener('visibilitychange', this.wakeRefreshListener);
    window.addEventListener('online', this.wakeRefreshListener);

    if(localStorage.getItem('currentUser')){
      this.updateLocalCal();

    }
  }

  async refreshTokenMessage(){

      Swal.fire({
            title: 'Do you want to continue this session?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Continue',
            cancelButtonText: "Logout",
            timer: ConfigConstants.SESSION_EXPIRE_POPUP_TIME_SECONDS * 1000
          }).then(async (result) => {
            if (result.isConfirmed) {
              if (localStorage && localStorage.length>0 && !window.location.href.includes('apps/lead-management/lead-intake-form')
                && !window.location.href.includes('authentication/login-v2')) {
                  await this._authenticationService.refreshToken();
              }
              this.userIdle.resetTimer();
            } else if (result.dismiss === Swal.DismissReason.cancel) {
              this._authenticationService.logout();
              this._router.navigate(['/pages/authentication/login-v2']);
            }
          })

  }
  
  /**
   * Setup handlers for tab/browser close events
   * Note: We do NOT logout on page refresh to preserve user session on reload
   */
  setupSessionCloseHandlers(): void {
    // Handle browser/tab close
    window.addEventListener('beforeunload', (event) => {
      this.handleSessionClose();
    });

    // Handle tab visibility change (when switching to other tab)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Tab is hidden - optionally can trigger logout
        // this.handleSessionClose();
      } else {
        // Tab is visible again - can reset timer if needed
      }
    });

    // Handle page unload
    window.addEventListener('unload', () => {
      this.handleSessionClose();
    });
  }

  /**
   * Handle session close - logout user and set session flag to false
   */
  private async handleSessionClose(): Promise<void> {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (currentUser && currentUser.id) {
      try {
        // Call logout to handle device session logout
        await this._authenticationService.logout();
        // Session flag is cleared in logout method
      } catch (error) {
        console.error('Error during session close logout:', error);
      }
    }
  }

  async updateLocalCal() {

    let calEvents = await this._calendarService.filterEvents();
    let calendarEvents = [];
    calEvents.forEach(element => {
      if (element.extendedProps['reminder'] && !element.extendedProps['done']) {
        calendarEvents.push(element);
      }
    });
    sessionStorage.setItem('calendarEvents', JSON.stringify(calendarEvents))

    // Clear any previously-scheduled reminder poll. updateLocalCal() is invoked
    // from ngOnInit, dismissCal, and snooze — without this clear, each call
    // stacked a new 2-minute interval that ran forever.
    if (this.calendarReminderInterval) {
      clearInterval(this.calendarReminderInterval);
    }
    this.calendarReminderInterval = setInterval(() => {
      if (sessionStorage.calendarEvents) {
        this.events = JSON.parse(sessionStorage.calendarEvents).filter(ev => { if (new Date().getTime() > new Date(ev.extendedProps.reminderDateTime).getTime()) return ev });
        this.modalReference?.close();
        if (this.events.length > 0) {
          this.modalReference = this.modelService.open(this.calModal, {
            centered: true,
            windowClass: 'modal modal-primary',
            backdrop: 'static',
            size: 'lg'
          });
        }
      }
    }, 120000);
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    if (this.tokenExpiryInterval) {
      clearInterval(this.tokenExpiryInterval);
      this.tokenExpiryInterval = null;
    }
    if (this.calendarReminderInterval) {
      clearInterval(this.calendarReminderInterval);
      this.calendarReminderInterval = null;
    }
    if (this.idleActivityListener) {
      for (const event of this.idleActivityEvents) {
        document.body.removeEventListener(event, this.idleActivityListener);
      }
      this.idleActivityListener = null;
    }
    if (this.wakeRefreshListener) {
      document.removeEventListener('visibilitychange', this.wakeRefreshListener);
      window.removeEventListener('online', this.wakeRefreshListener);
      this.wakeRefreshListener = null;
    }
    if (this.storageActivityListener) {
      window.removeEventListener('storage', this.storageActivityListener);
      this.storageActivityListener = null;
    }
  }
  async dismissCal(ev) {
    let upEvent = this._calendarService._updateFormaEvent(ev);
    upEvent['done'] = true;
    await this._calendarService.snoozeDismissEvent(upEvent['id'], upEvent);
    let index = this.events.findIndex(obj => obj.id == ev.id);
    this.events.splice(index, 1)
    if (this.events.length == 0) {
      this.modelService.dismissAll();  
      }
    this.updateLocalCal();
    // this.modelService.dismissAll();

  }

  async snooze(event){
    event.extendedProps.reminderDateTime =  new Date(new Date().getTime() + parseInt(event.period)*60*1000).toISOString();
    let upEvent = this._calendarService._updateFormaEvent(event);
    await this._calendarService.snoozeDismissEvent(upEvent['id'], upEvent);
    let index = this.events.findIndex(obj => obj.id == event.id);
    this.events.splice(index, 1)
    if (this.events.length == 0) {
      this.modelService.dismissAll();  
      }
      this.updateLocalCal();
    // this.modelService.dismissAll();
  }

  async dismissAll(ev){

  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set Length of Badge Title
   * @returns
   */
  getTitleCount(currentUser): string {    
    if (currentUser) {
      if(currentUser.role === 'Admin') {
        return '2';
      } else if(currentUser.role === 'Client' || currentUser.role === 'Employee') {
        return '1';
      }
    }
    return '';
  }
}
